import { aggregateMetricsAsPool } from '@foresightdata/metrics-calculator';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import { IPortfolioTreeRow } from './computeTreeRows';

/** The leaf keys among the grid's post-filter rows — what `repoolTreeRows` narrows to. */
export function visibleTreeLeafKeys(
  visibleRows: readonly (IPortfolioTreeRow | null | undefined)[]
): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const row of visibleRows) {
    if (row?.treeKind === 'company') {
      keys.add(row.treeKey);
    }
  }
  return keys;
}

/**
 * Ancestor rows re-pooled over only the visible leaves (FD-4988).
 *
 * Node rows carry pools precomputed over every leaf beneath them; grid filters only pick
 * which rows are shown. Filtered to one company, the leaves narrow but a fund row keeps
 * its full-portfolio number — this recomputes each node from the leaves the filter kept,
 * against the same mode-aware listing the build pooled from (`leafKeysByNode`).
 *
 * Register-valued synthetics are dropped while a filter is active: a leaf filter can
 * never match a leafless vehicle, so its register value has no place in the narrowed
 * roll-up. Leaf rows keep object identity — the KPI atoms and the publish-echo guard
 * both key on it — and full visibility returns the input array itself, so the no-filter
 * path costs nothing and triggers no downstream work.
 */
export function repoolTreeRows(
  rows: IPortfolioTreeRow[],
  leafKeysByNode: ReadonlyMap<string, readonly string[]>,
  visibleLeafKeys: ReadonlySet<string>,
  asOfDate: Date
): IPortfolioTreeRow[] {
  const leafByKey = new Map<string, IPortfolioTreeRow>();
  let allVisible = true;
  for (const row of rows) {
    if (row.treeKind === 'company') {
      leafByKey.set(row.treeKey, row);
      if (!visibleLeafKeys.has(row.treeKey)) {
        allVisible = false;
      }
    }
  }
  if (allVisible) {
    return rows;
  }

  return rows.map((row) => {
    if (row.treeKind === 'company') {
      return row;
    }
    const visible = (leafKeysByNode.get(row.treeKey) ?? [])
      .filter((key) => visibleLeafKeys.has(key))
      .map((key) => leafByKey.get(key))
      .filter((leaf): leaf is IPortfolioTreeRow => leaf != null);
    // Nothing visible beneath it: the grid hides the row, so its number is never read.
    if (visible.length === 0) {
      return row;
    }
    const pool = aggregateMetricsAsPool(
      visible as unknown as Parameters<typeof aggregateMetricsAsPool>[0],
      asOfDate
    );
    if (!pool) {
      return row;
    }
    return {
      ...(pool as unknown as IMetricsDataModel),
      treeKey: row.treeKey,
      treePath: row.treePath,
      treeNodeRef: row.treeNodeRef,
      treeKind: row.treeKind,
      treeLabel: row.treeLabel,
      treeSource: row.treeSource,
      treeIncomplete: row.treeIncomplete,
      treeRowCount: visible.length,
      treeLedgerRowCount: visible.length,
    } as IPortfolioTreeRow;
  });
}
